import { findBusStops } from '@/services/bus-stops-service'
import { addAlert } from '@/utilities/helpers'
import { formatTime } from '@smarttransit/common'

export default {
  name: 'routes-events-bus-stop-pickup-times',
  props: {
    pickupTimes: {
      type: Array,
      default () {
        return []
      }
    },
    isAdmin: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    busStopOnly: {
      type: Boolean,
      default: false
    },
    timeOnly: {
      type: Boolean,
      default: false
    }
  },
  data () {
    return {
      busStopNamesById: {},
      busStopNamesLoading: false,
      pickupTimesParsed: []
    }
  },
  computed: {
    isTimeOnly () {
      return this.$props.timeOnly
    },
    isBusStopOnly () {
      return this.$props.busStopOnly
    },
    shouldGroupOutbound () {
      // Only group the outbound “time + stop” mode.
      return !this.isTimeOnly && !this.isBusStopOnly
    },
    pickupTimesGrouped () {
      if (!this.shouldGroupOutbound) {
        return []
      }

      const entries = Array.isArray(this.pickupTimesParsed) ? this.pickupTimesParsed : []
      const groupsByKey = {}

      for (let i = 0; i < entries.length; i++) {
        const pickup = entries[i]
        if (!pickup) {
          continue
        }

        const key = this.getPickupGroupKey(pickup, i)

        if (!groupsByKey[key]) {
          groupsByKey[key] = {
            key,
            firstIndex: i,
            busStopId: pickup.busStopId || null,
            label: pickup.label || null,
            displayName: this.getPickupDisplayName(pickup),
            displayNameTruncated: this.getTruncatedPickupDisplayName(pickup),
            timesRaw: [],
            timesDisplay: []
          }
        }

        const group = groupsByKey[key]
        if (i < group.firstIndex) {
          group.firstIndex = i
        }

        if (pickup.time) {
          group.timesRaw.push(pickup.time)
          group.timesDisplay.push(pickup.displayTime || '')
        }
      }

      const groups = Object.keys(groupsByKey).map(function (k) { return groupsByKey[k] })

      // Normalize: sort & de-dupe times
      for (let j = 0; j < groups.length; j++) {
        const g = groups[j]

        // Build pairs so we can sort by raw HH:mm but display formatted time
        const pairs = []
        for (let t = 0; t < g.timesRaw.length; t++) {
          pairs.push({ raw: g.timesRaw[t], display: g.timesDisplay[t] })
        }

        pairs.sort(function (a, b) {
          if (a.raw < b.raw) return -1
          if (a.raw > b.raw) return 1
          return 0
        })

        const deduped = []
        const seen = {}
        for (let p = 0; p < pairs.length; p++) {
          const raw = pairs[p].raw
          if (raw && !seen[raw]) {
            seen[raw] = true
            deduped.push(pairs[p])
          }
        }

        g.timesRaw = deduped.map(function (x) { return x.raw })
        g.timesDisplay = deduped.map(function (x) { return x.display })
        g.timesDisplayText = g.timesDisplay.filter(function (x) { return Boolean(x) }).join(', ')
      }

      // Sort groups by stop/label display name for stable UI
      groups.sort(function (a, b) {
        const an = (a.displayName || '').toLowerCase()
        const bn = (b.displayName || '').toLowerCase()
        if (an < bn) return -1
        if (an > bn) return 1
        return 0
      })

      return groups
    }
  },
  watch: {
    pickupTimes: {
      handler: function () {
        this.loadBusStopNamesForCurrentEvent()
          .then(() => {
            this.pickupTimesParsed = this.pickupTimes.map((pickup) => {
              return {
                ...pickup,
                displayName: this.getPickupDisplayName(pickup),
                displayTime: formatTime(pickup.time)
              }
            })
          })
      },
      immediate: true
    }
  },
  methods: {
    getPickupGroupKey (pickup, index) {
      if (!pickup) {
        return 'idx:' + index
      }

      if (pickup.busStopId) {
        return 'stop:' + pickup.busStopId
      }

      if (pickup.label) {
        return 'label:' + pickup.label
      }

      return 'idx:' + index
    },
    async loadBusStopNamesForCurrentEvent () {
      const entries = Array.isArray(this.pickupTimes) ? this.pickupTimes : []

      const idsToFetchMap = {}
      for (let i = 0; i < entries.length; i++) {
        const pickup = entries[i]
        if (!pickup || !pickup.busStopId || pickup.label) {
          continue
        }

        const id = pickup.busStopId
        if (!this.busStopNamesById[id]) {
          idsToFetchMap[id] = true
        }
      }

      const idsToFetch = Object.keys(idsToFetchMap).map(function (k) {
        return Number(k)
      }).filter(function (v) { return !isNaN(v) })

      if (!idsToFetch.length) {
        return
      }

      this.busStopNamesLoading = true
      try {
        const filter = {
          where: { id: { inq: idsToFetch } },
          limit: idsToFetch.length
        }

        const results = await findBusStops(filter)

        if (Array.isArray(results)) {
          const namesById = { ...this.busStopNamesById }

          for (let j = 0; j < results.length; j++) {
            const stop = results[j]
            if (stop && stop.id && stop.name) {
              namesById[stop.id] = stop.name
            }
          }

          this.busStopNamesById = namesById
        }
      } catch (err) {
        addAlert({
          message: err,
          type: 'error'
        })
      } finally {
        this.busStopNamesLoading = false
      }
    },
    getPickupDisplayName (pickup) {
      if (!pickup) {
        return ''
      }

      if (pickup.label) {
        return pickup.label
      }

      if (pickup.busStopId && this.busStopNamesById[pickup.busStopId]) {
        return this.busStopNamesById[pickup.busStopId]
      }

      if (pickup.busStopId) {
        return 'Stop #' + pickup.busStopId
      }

      return 'Unknown stop'
    },
    getTruncatedPickupDisplayName (pickup) {
      const name = this.getPickupDisplayName(pickup) || ''

      if (name.length > 80) {
        return name.slice(0, 80) + '…'
      }

      return name
    },
    onAdd () {
      this.$emit('add')
    },
    onEdit (index) {
      this.$emit('edit', index)
    },
    onDelete (index) {
      this.$emit('delete', index)
    }
  }
}
